import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  PhoneCall, Plus, Pencil, Trash2, Loader2, X, Mic, CalendarClock,
  Clock, Phone, ArrowRight, Sparkles, BookOpen, ListChecks, BarChart3, Play, Square,
  CheckCircle2, Lock, Settings as SettingsIcon,
} from "lucide-react";
import { toast } from "sonner";
import { usePageHeader } from "../contexts/PageHeaderContext";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { aiAgentsApi, usersApi } from "../lib/api";
import { useAuth, formatApiErrorDetail } from "../contexts/AuthContext";
import KnowledgeBase from "../components/aiagents/KnowledgeBase";
import CallQueue from "../components/aiagents/CallQueue";
import Receptionist from "../components/aiagents/Receptionist";
import Reports from "../components/aiagents/Reports";
import CallSummarySettings from "../components/aiagents/CallSummarySettings";

const STATUS_PILL = {
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  paused: "bg-amber-50 text-amber-700 border-amber-200",
  draft: "bg-slate-100 text-slate-600 border-slate-200",
};

const err = (e, fb) => toast.error(formatApiErrorDetail(e?.response?.data?.detail) || fb);

const AGENT_TYPES = [
  { key: "estimate_followup", label: "Estimate Follow-Up", available: true },
  { key: "membership_sales", label: "Membership Renewals", available: true },
  { key: "scheduling", label: "Appointment Scheduling", available: false },
  { key: "reactivation", label: "Customer Reactivation", available: false },
  { key: "collections", label: "Payment Collections", available: false },
  { key: "customer_service", label: "Customer Service", available: false },
];
const AGENT_TYPE_LABEL = Object.fromEntries(AGENT_TYPES.map((t) => [t.key, t.label]));

export default function AiAgents() {
  const { user, hasModule } = useAuth();
  const isAdmin = user && user.role === "admin";
  const [tab, setTab] = useState("agents");
  const [meta, setMeta] = useState(null);
  const [agents, setAgents] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editAgent, setEditAgent] = useState(null); // agent obj | {} | null
  const [editCampaign, setEditCampaign] = useState(null);

  // Support deep-linking straight to a sub-tab, e.g. from the onboarding checklist.
  useEffect(() => {
    const tabParam = new URLSearchParams(window.location.search).get("tab");
    if (tabParam) {
      setTab(tabParam);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [m, a, c] = await Promise.all([
        aiAgentsApi.meta(),
        aiAgentsApi.listAgents(),
        aiAgentsApi.listCampaigns(),
      ]);
      setMeta(m); setAgents(a); setCampaigns(c);
    } catch (e) {
      err(e, "Failed to load AI agents.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    let active = true;
    (async () => { await Promise.resolve(); if (active) load(); })();
    return () => { active = false; };
  }, []);

  const removeAgent = async (id) => {
    if (!window.confirm("Delete this agent?")) return;
    try { await aiAgentsApi.removeAgent(id); toast.success("Agent deleted."); load(); }
    catch (e) { err(e, "Could not delete agent."); }
  };
  const removeCampaign = async (id) => {
    if (!window.confirm("Delete this campaign?")) return;
    try { await aiAgentsApi.removeCampaign(id); toast.success("Campaign deleted."); load(); }
    catch (e) { err(e, "Could not delete campaign."); }
  };
  const toggleStatus = async (kind, row) => {
    const next = row.status === "active" ? "paused" : "active";
    try {
      if (kind === "agent") await aiAgentsApi.updateAgent(row.id, { status: next });
      else await aiAgentsApi.updateCampaign(row.id, { status: next });
      load();
    } catch (e) { err(e, "Could not update status."); }
  };

  usePageHeader({
    title: "AI Voice Agents",
    subtitle: "Automated outbound AI phone calls — Estimate Follow-Up & Membership Renewals",
  });

  return (
    <div className="min-h-screen bg-slate-50" data-testid="ai-agents-page">
      <div className="px-6 py-6 max-w-6xl mx-auto">
        {/* Tabs */}
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="inline-flex rounded-lg bg-white border border-slate-200 p-1">
            {[["agents", "Agents", Mic], ["campaigns", "Campaigns", CalendarClock], ["receptionist", "Receptionist", PhoneCall], ["knowledge", "Knowledge Base", BookOpen], ["queue", "Call Queue", ListChecks], ["settings", "Settings", SettingsIcon], ["reports", "Reports", BarChart3]]
              .filter(([k]) => k !== "knowledge" || hasModule("ai_knowledge_base"))
              .filter(([k]) => k !== "queue" || hasModule("outbound_call_queue") || hasModule("call_queue_lifecycle"))
              .map(([k, label, Icon]) => (
              <button
                key={k} data-testid={`ai-tab-${k}`}
                onClick={() => setTab(k)}
                className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md text-[13.5px] font-medium transition-colors ${tab === k ? "bg-indigo-600 text-white" : "text-slate-500 hover:text-slate-800"}`}
              >
                <Icon className="h-4 w-4" /> {label}
              </button>
            ))}
          </div>
          {isAdmin && (
            tab === "agents" ? (
              <Button data-testid="new-agent-btn" onClick={() => setEditAgent({})} className="bg-indigo-600 hover:bg-indigo-700">
                <Plus className="h-4 w-4 mr-1.5" /> New agent
              </Button>
            ) : tab === "campaigns" ? (
              <Button
                data-testid="new-campaign-btn"
                onClick={() => agents.length ? setEditCampaign({}) : toast.error("Create an agent first.")}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                <Plus className="h-4 w-4 mr-1.5" /> New campaign
              </Button>
            ) : null
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24"><Loader2 className="h-7 w-7 text-indigo-500 animate-spin" /></div>
        ) : tab === "agents" ? (
          <AgentsList agents={agents} isAdmin={isAdmin} onEdit={setEditAgent} onDelete={removeAgent} onToggle={(a) => toggleStatus("agent", a)} />
        ) : tab === "campaigns" ? (
          <CampaignsList campaigns={campaigns} isAdmin={isAdmin} onEdit={setEditCampaign} onDelete={removeCampaign} onToggle={(c) => toggleStatus("campaign", c)} />
        ) : tab === "receptionist" ? (
          <Receptionist meta={meta || {}} isAdmin={isAdmin} />
        ) : tab === "knowledge" && hasModule("ai_knowledge_base") ? (
          <KnowledgeBase meta={meta || {}} isAdmin={isAdmin} />
        ) : tab === "queue" && (hasModule("outbound_call_queue") || hasModule("call_queue_lifecycle")) ? (
          <CallQueue campaigns={campaigns} isAdmin={isAdmin} />
        ) : tab === "settings" ? (
          <CallSummarySettings isAdmin={isAdmin} />
        ) : (
          <Reports agents={agents} />
        )}
      </div>

      {editAgent && meta && (
        <AgentEditor meta={meta} agent={editAgent} onClose={() => setEditAgent(null)} onSaved={() => { setEditAgent(null); load(); }} />
      )}
      {editCampaign && meta && (
        <CampaignEditor meta={meta} campaign={editCampaign} agents={agents} onClose={() => setEditCampaign(null)} onSaved={() => { setEditCampaign(null); load(); }} />
      )}
    </div>
  );
}

// ---------------- Agents ----------------
function AgentsList({ agents, isAdmin, onEdit, onDelete, onToggle }) {
  if (!agents.length) {
    return <EmptyState icon={Mic} title="No agents yet" hint={isAdmin ? "Create your first AI voice agent (e.g. “Sarah”) and write her scripts." : "No agents have been configured."} />;
  }
  return (
    <div className="grid gap-3 sm:grid-cols-2" data-testid="agents-list">
      {agents.map((a) => (
        <div key={a.id} className="rounded-xl border border-slate-200 bg-white p-4 hover:shadow-sm transition-shadow" data-testid={`agent-card-${a.id}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center shrink-0">
                <Mic className="h-5 w-5 text-indigo-600" />
              </div>
              <div className="min-w-0">
                <div className="font-semibold text-slate-800 truncate">{a.name}</div>
                <div className="text-[12px] text-slate-500">{a.voiceName || "No voice"} · {AGENT_TYPE_LABEL[a.agentType] || "Estimate Follow-Up"}</div>
              </div>
            </div>
            <span className={`text-[11px] px-2 py-0.5 rounded-full border ${STATUS_PILL[a.status] || STATUS_PILL.draft}`}>{a.status}</span>
          </div>
          {a.goal && <p className="text-[13px] text-slate-600 mt-3 line-clamp-2">{a.goal}</p>}
          {a.offerText && <div className="mt-2 inline-flex items-center gap-1 text-[12px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5"><Sparkles className="h-3 w-3" /> {a.offerText}</div>}
          {isAdmin && (
            <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100">
              <label className="flex items-center gap-2 text-[12.5px] text-slate-600">
                <Switch checked={a.status === "active"} onCheckedChange={() => onToggle(a)} data-testid={`agent-toggle-${a.id}`} />
                {a.status === "active" ? "Active" : "Off"}
              </label>
              <div className="flex items-center gap-1">
                <button onClick={() => onEdit(a)} data-testid={`agent-edit-${a.id}`} className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => onDelete(a.id)} data-testid={`agent-delete-${a.id}`} className="p-1.5 rounded-md text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ---------------- Campaigns ----------------
function CampaignsList({ campaigns, isAdmin, onEdit, onDelete, onToggle }) {
  if (!campaigns.length) {
    return <EmptyState icon={CalendarClock} title="No campaigns yet" hint={isAdmin ? "A campaign decides when its agent calls — unsold estimates (Estimate Follow-Up) or loaded leads (Membership Renewals)." : "No campaigns configured."} />;
  }
  return (
    <div className="space-y-3" data-testid="campaigns-list">
      {campaigns.map((c) => (
        <div key={c.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`campaign-card-${c.id}`}>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <div className="font-semibold text-slate-800">{c.name}</div>
                <span className={`text-[11px] px-2 py-0.5 rounded-full border ${STATUS_PILL[c.status] || STATUS_PILL.draft}`}>{c.status}</span>
                <span className="text-[11px] px-2 py-0.5 rounded-full border border-indigo-200 bg-indigo-50 text-indigo-700" data-testid={`campaign-type-pill-${c.id}`}>
                  {AGENT_TYPE_LABEL[c.agentType] || AGENT_TYPE_LABEL.estimate_followup}
                </span>
              </div>
              <div className="text-[12.5px] text-slate-500 mt-0.5">Agent: <span className="text-slate-700 font-medium">{c.agentName || "—"}</span></div>
            </div>
            {isAdmin && (
              <div className="flex items-center gap-1">
                <label className="flex items-center gap-2 text-[12.5px] text-slate-600 mr-1">
                  <Switch checked={c.status === "active"} onCheckedChange={() => onToggle(c)} data-testid={`campaign-toggle-${c.id}`} />
                </label>
                <button onClick={() => onEdit(c)} data-testid={`campaign-edit-${c.id}`} className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => onDelete(c.id)} data-testid={`campaign-delete-${c.id}`} className="p-1.5 rounded-md text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            {(c.attemptDays || []).map((d, i) => (
              <React.Fragment key={i}>
                {i > 0 && <ArrowRight className="h-3.5 w-3.5 text-slate-300" />}
                <span className="inline-flex items-center gap-1 text-[12px] bg-slate-100 text-slate-700 rounded-full px-2.5 py-1">
                  <Phone className="h-3 w-3" /> Day {d}
                </span>
              </React.Fragment>
            ))}
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-1 mt-3 text-[12px] text-slate-500">
            <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {c.callWindowStart}–{c.callWindowEnd}</span>
            <span>{c.weekendCallingAllowed ? "Weekends on" : "Weekdays only"}</span>
            <span>Voicemail: {(c.voicemailBehavior || "").replace(/_/g, " ")}</span>
            <span>{c.timezone}</span>
            {c.transferNumber && <span>Transfer: {c.transferNumber}</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ icon: Icon, title, hint }) {
  return (
    <div className="rounded-xl border border-dashed border-slate-300 bg-white py-16 text-center" data-testid="empty-state">
      <Icon className="h-9 w-9 text-slate-300 mx-auto mb-3" />
      <div className="font-semibold text-slate-700">{title}</div>
      <div className="text-[13px] text-slate-500 mt-1 max-w-md mx-auto">{hint}</div>
    </div>
  );
}

// ---------------- Editors ----------------
function Field({ label, children, hint }) {
  return (
    <div>
      <label className="block text-[12.5px] font-medium text-slate-700 mb-1">{label}</label>
      {children}
      {hint && <div className="text-[11.5px] text-slate-400 mt-1">{hint}</div>}
    </div>
  );
}

const inputCls = "w-full h-9 px-3 rounded-lg border border-slate-300 text-[13.5px] text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400";
const areaCls = "w-full px-3 py-2 rounded-lg border border-slate-300 text-[13px] text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400 resize-y";

function ScriptField({ label, value, onChange, placeholders, rows = 3, testid }) {
  return (
    <Field label={label}>
      <textarea data-testid={testid} rows={rows} value={value} onChange={(e) => onChange(e.target.value)} className={areaCls} />
      <div className="flex flex-wrap gap-1 mt-1.5">
        {placeholders.map((p) => (
          <button key={p} type="button" onClick={() => onChange(`${value || ""}${value && !value.endsWith(" ") ? " " : ""}${p}`)}
            className="text-[11px] bg-slate-100 hover:bg-indigo-100 text-slate-600 hover:text-indigo-700 rounded px-1.5 py-0.5 transition-colors">
            {p}
          </button>
        ))}
      </div>
    </Field>
  );
}

function Drawer({ title, onClose, children, footer }) {
  return (
    <div className="fixed inset-0 z-[100] flex" data-testid="ai-editor-drawer">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <div className="w-full max-w-xl bg-white h-full overflow-y-auto shadow-2xl flex flex-col">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3.5 flex items-center justify-between z-10">
          <h3 className="font-semibold text-slate-800">{title}</h3>
          <button onClick={onClose} data-testid="ai-editor-close" className="p-1.5 rounded-md text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
        </div>
        <div className="px-5 py-4 space-y-4 flex-1">{children}</div>
        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-5 py-3 flex items-center justify-end gap-2">{footer}</div>
      </div>
    </div>
  );
}

function AgentEditor({ meta, agent, onClose, onSaved }) {
  const { hasModule } = useAuth();
  const isNew = !agent.id;
  const initialType = agent.agentType || "estimate_followup";
  const ds = meta.defaultScripts?.[initialType] || {};
  const [f, setF] = useState(() => ({
    name: agent.name || "",
    status: agent.status || "draft",
    agentType: initialType,
    voiceId: agent.voiceId || (meta.voices[0]?.voiceId || ""),
    goal: agent.goal ?? (isNew ? ds.goal : ""),
    offerText: agent.offerText ?? (isNew ? ds.offerText : ""),
    offer: agent.offer || { enabled: false, type: "percentage", value: 10, label: "", expiryDays: 7 },
    openingScript: agent.openingScript ?? (isNew ? ds.openingScript : ""),
    offerScript: agent.offerScript ?? (isNew ? ds.offerScript : ""),
    objectionHandling: agent.objectionHandling ?? (isNew ? ds.objectionHandling : ""),
    transferRules: agent.transferRules ?? (isNew ? ds.transferRules : ""),
    escalationRules: agent.escalationRules ?? (isNew ? ds.escalationRules : ""),
    closingScript: agent.closingScript ?? (isNew ? ds.closingScript : ""),
  }));
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const ph = meta.scriptPlaceholdersByType?.[f.agentType] || meta.scriptPlaceholders || [];

  const selectType = (key) => {
    if (key === f.agentType) return;
    // Fresh, unsaved agent: swap in that type's starter scripts too.
    const nd = isNew ? (meta.defaultScripts?.[key] || {}) : null;
    setF((s) => ({
      ...s, agentType: key,
      ...(nd ? {
        goal: nd.goal || "", offerText: nd.offerText || "", openingScript: nd.openingScript || "",
        offerScript: nd.offerScript || "", objectionHandling: nd.objectionHandling || "",
        transferRules: nd.transferRules || "", escalationRules: nd.escalationRules || "", closingScript: nd.closingScript || "",
      } : {}),
    }));
  };

  const save = async () => {
    if (!f.name.trim()) { toast.error("Agent name is required."); return; }
    const voiceName = (meta.voices.find((v) => v.voiceId === f.voiceId) || {}).name || "";
    const body = { ...f, voiceName };
    setSaving(true);
    try {
      if (isNew) await aiAgentsApi.createAgent(body);
      else await aiAgentsApi.updateAgent(agent.id, body);
      toast.success(isNew ? "Agent created." : "Agent saved.");
      onSaved();
    } catch (e) { err(e, "Could not save agent."); }
    finally { setSaving(false); }
  };

  return (
    <Drawer
      title={isNew ? "New AI agent" : `Edit ${agent.name}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="agent-save-btn" className="bg-indigo-600 hover:bg-indigo-700">
            {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} {isNew ? "Create agent" : "Save"}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-3">
        <Field label="Agent name">
          <input data-testid="agent-name-input" value={f.name} onChange={(e) => set("name")(e.target.value)} placeholder="Sarah" className={inputCls} />
        </Field>
        <Field label="Status">
          <select data-testid="agent-status-select" value={f.status} onChange={(e) => set("status")(e.target.value)} className={inputCls}>
            <option value="draft">Draft</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
          </select>
        </Field>
      </div>
      <Field label="Agent type">
        <div className="grid grid-cols-2 gap-2" data-testid="agent-type-grid">
          {AGENT_TYPES.map((t) => (
            <button
              type="button"
              key={t.key}
              data-testid={`agent-type-${t.key}`}
              disabled={!t.available}
              onClick={() => t.available && selectType(t.key)}
              className={`flex flex-col gap-1.5 rounded-lg border px-3 py-2 text-[12.5px] text-left transition-colors ${
                !t.available
                  ? "border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed"
                  : f.agentType === t.key
                    ? "border-indigo-500 bg-indigo-50 text-indigo-700 font-semibold ring-2 ring-indigo-200"
                    : "border-indigo-200 bg-white text-indigo-600 font-semibold hover:border-indigo-400 hover:bg-indigo-50"
              }`}
            >
              <span>{t.label}</span>
              {t.available ? (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-indigo-500">
                  <CheckCircle2 className="h-3 w-3" /> {f.agentType === t.key ? "Selected" : "Available"}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[9.5px] font-bold uppercase tracking-wider bg-slate-200 text-slate-500 rounded-full px-1.5 py-0.5 w-fit">
                  <Lock className="h-2.5 w-2.5" /> Coming soon
                </span>
              )}
            </button>
          ))}
        </div>
        <div className="text-[11.5px] text-slate-400 mt-1.5" data-testid="agent-type-note">
          This decides which Call Queue tab campaigns using this agent appear in — the other types above are coming soon.
        </div>
      </Field>
      <Field label="Voice" hint={meta.voicesLive ? "Live ElevenLabs voices — press play to hear a sample." : "Preset voice options."}>
        <div className="flex items-center gap-2">
          <select data-testid="agent-voice-select" value={f.voiceId} onChange={(e) => set("voiceId")(e.target.value)} className={`${inputCls} flex-1`}>
            {meta.voices.map((v) => <option key={v.voiceId} value={v.voiceId}>{v.name}{v.description ? ` — ${v.description}` : ""}</option>)}
          </select>
          <VoicePreviewButton voice={meta.voices.find((v) => v.voiceId === f.voiceId)} />
        </div>
      </Field>
      <ScriptField label="Goal" testid="agent-goal" rows={2} value={f.goal} onChange={set("goal")} placeholders={[]} />
      <Field label="Offer (short)">
        <input data-testid="agent-offer-input" value={f.offerText} onChange={(e) => set("offerText")(e.target.value)} placeholder="10% off if booked within 7 days" className={inputCls} />
      </Field>
      {f.agentType === "estimate_followup" && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 space-y-3" data-testid="agent-offer-config">
          <div className="flex items-center justify-between">
            <div className="text-[12px] font-semibold text-emerald-800 uppercase tracking-wider">Live discount on outbound calls</div>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                data-testid="agent-offer-enabled-toggle"
                checked={f.offer.enabled}
                onChange={(e) => set("offer")({ ...f.offer, enabled: e.target.checked })}
                disabled={!hasModule("live_discount")}
                className="h-4 w-4 accent-emerald-600 disabled:opacity-40"
              />
              <span className="text-[12px] text-emerald-700 font-medium">{f.offer.enabled ? "On" : "Off"}</span>
            </label>
          </div>
          {!hasModule("live_discount") && (
            <div className="text-[11.5px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5" data-testid="agent-offer-locked-note">
              Ask your account admin to enable the <span className="font-semibold">Live Discount</span> module for your plan.
            </div>
          )}
          <div className="text-[11.5px] text-emerald-700/80">
            When on, the AI can offer this discount live during outbound estimate follow-up calls. The exact amount is always calculated by the backend — the AI never does the math.
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Discount %">
              <input
                data-testid="agent-offer-value-input"
                type="number" min={0} max={100} step={1}
                value={f.offer.value}
                onChange={(e) => set("offer")({ ...f.offer, value: Number(e.target.value) })}
                disabled={!f.offer.enabled || !hasModule("live_discount")}
                className={inputCls}
              />
            </Field>
            <Field label="Expires after (days)" hint="Counted from when the estimate was issued.">
              <input
                data-testid="agent-offer-expiry-input"
                type="number" min={1} max={90} step={1}
                value={f.offer.expiryDays}
                onChange={(e) => set("offer")({ ...f.offer, expiryDays: Number(e.target.value) })}
                disabled={!f.offer.enabled || !hasModule("live_discount")}
                className={inputCls}
              />
            </Field>
          </div>
          <Field label="Customer-facing label" hint="Shown on the follow-up email when the discount is applied.">
            <input
              data-testid="agent-offer-label-input"
              value={f.offer.label}
              onChange={(e) => set("offer")({ ...f.offer, label: e.target.value })}
              placeholder="10% off if you book this week"
              disabled={!f.offer.enabled || !hasModule("live_discount")}
              className={inputCls}
            />
          </Field>
        </div>
      )}
      <div className="pt-1 text-[12px] font-semibold text-slate-500 uppercase tracking-wider">Conversation scripts</div>
      <ScriptField label="Opening script" testid="agent-opening" value={f.openingScript} onChange={set("openingScript")} placeholders={ph} />
      <ScriptField label="Offer script" testid="agent-offerscript" value={f.offerScript} onChange={set("offerScript")} placeholders={ph} />
      <ScriptField label="Objection handling" testid="agent-objections" rows={4} value={f.objectionHandling} onChange={set("objectionHandling")} placeholders={[]} />
      <ScriptField label="Transfer rules" testid="agent-transfer" rows={2} value={f.transferRules} onChange={set("transferRules")} placeholders={[]} />
      <ScriptField label="Escalation rules" testid="agent-escalation" rows={2} value={f.escalationRules} onChange={set("escalationRules")} placeholders={[]} />
      <ScriptField label="Closing script" testid="agent-closing" value={f.closingScript} onChange={set("closingScript")} placeholders={ph} />
    </Drawer>
  );
}

function VoicePreviewButton({ voice }) {
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  useEffect(() => () => audioRef.current?.pause(), []);
  useEffect(() => { audioRef.current?.pause(); setPlaying(false); }, [voice?.voiceId]);
  if (!voice?.previewUrl) return null;
  const toggle = () => {
    if (playing) { audioRef.current?.pause(); setPlaying(false); return; }
    audioRef.current?.pause();
    const a = new Audio(voice.previewUrl);
    audioRef.current = a;
    a.onended = () => setPlaying(false);
    a.play().catch(() => setPlaying(false));
    setPlaying(true);
  };
  return (
    <button type="button" onClick={toggle} data-testid="agent-voice-preview" title="Preview this voice"
      className="h-12 w-12 shrink-0 inline-flex items-center justify-center rounded-md bg-transparent transition-colors">
      {playing ? <Square className="h-8 w-8 text-white" /> : <Play className="h-8 w-8 text-white" />}
    </button>
  );
}

function CampaignEditor({ meta, campaign, agents, onClose, onSaved }) {
  const isNew = !campaign.id;
  const dc = meta.defaultCampaign || {};
  const [f, setF] = useState(() => ({
    name: campaign.name || "",
    agentId: campaign.agentId || (agents[0]?.id || ""),
    status: campaign.status || "paused",
    attemptDays: campaign.attemptDays || dc.attemptDays || [2, 5, 10],
    callWindowStart: campaign.callWindowStart || dc.callWindowStart || "08:00",
    callWindowEnd: campaign.callWindowEnd || dc.callWindowEnd || "18:00",
    weekendCallingAllowed: campaign.weekendCallingAllowed ?? dc.weekendCallingAllowed ?? false,
    voicemailBehavior: campaign.voicemailBehavior || dc.voicemailBehavior || "leave_message",
    timezone: campaign.timezone || dc.timezone || "America/New_York",
    transferNumber: campaign.transferNumber || "",
    autoSendOnAccept: campaign.autoSendOnAccept ?? true,
    postPaymentTaskEnabled: campaign.postPaymentTaskEnabled ?? false,
    postPaymentTaskAssigneeId: campaign.postPaymentTaskAssigneeId || "",
    postPaymentTaskDueDays: campaign.postPaymentTaskDueDays ?? 1,
  }));
  const [saving, setSaving] = useState(false);
  const [staff, setStaff] = useState([]);
  useEffect(() => {
    usersApi.list().then((u) => setStaff((u || []).filter((x) => x.status !== "invited"))).catch(() => {});
  }, []);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const selectedAgentType = (agents.find((a) => a.id === f.agentId) || {}).agentType || "estimate_followup";

  const setDay = (i, v) => setF((s) => { const d = [...s.attemptDays]; d[i] = Math.max(0, parseInt(v || "0", 10)); return { ...s, attemptDays: d }; });
  const addDay = () => setF((s) => ({ ...s, attemptDays: [...s.attemptDays, (s.attemptDays.at(-1) || 0) + 3] }));
  const removeDay = (i) => setF((s) => ({ ...s, attemptDays: s.attemptDays.filter((_, j) => j !== i) }));

  const save = async () => {
    if (!f.name.trim()) { toast.error("Campaign name is required."); return; }
    if (!f.agentId) { toast.error("Select an agent."); return; }
    setSaving(true);
    try {
      if (isNew) await aiAgentsApi.createCampaign(f);
      else await aiAgentsApi.updateCampaign(campaign.id, f);
      toast.success(isNew ? "Campaign created." : "Campaign saved.");
      onSaved();
    } catch (e) { err(e, "Could not save campaign."); }
    finally { setSaving(false); }
  };

  return (
    <Drawer
      title={isNew ? "New campaign" : `Edit ${campaign.name}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="campaign-save-btn" className="bg-indigo-600 hover:bg-indigo-700">
            {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} {isNew ? "Create campaign" : "Save"}
          </Button>
        </>
      }
    >
      <Field label="Campaign name">
        <input data-testid="campaign-name-input" value={f.name} onChange={(e) => set("name")(e.target.value)} placeholder="Unsold Estimates Follow-Up" className={inputCls} />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Agent" hint={`${AGENT_TYPE_LABEL[selectedAgentType] || "Estimate Follow-Up"} — decides which Call Queue tab this campaign appears in.`}>
          <select data-testid="campaign-agent-select" value={f.agentId} onChange={(e) => set("agentId")(e.target.value)} className={inputCls}>
            {agents.map((a) => <option key={a.id} value={a.id}>{a.name} · {AGENT_TYPE_LABEL[a.agentType] || "Estimate Follow-Up"}</option>)}
          </select>
        </Field>
        <Field label="Status">
          <select data-testid="campaign-status-select" value={f.status} onChange={(e) => set("status")(e.target.value)} className={inputCls}>
            <option value="paused">Paused</option>
            <option value="active">Active</option>
            <option value="draft">Draft</option>
          </select>
        </Field>
      </div>

      <Field label="Call schedule" hint={selectedAgentType === "membership_sales" ? "Days after the customer list was loaded. Each row is a call attempt." : "Days after the estimate is sent. Each row is a call attempt."}>
        <div className="space-y-2" data-testid="attempt-days">
          {f.attemptDays.map((d, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="text-[12.5px] text-slate-500 w-16">Attempt {i + 1}</span>
              <span className="text-[12.5px] text-slate-500">Day</span>
              <input type="number" min="0" value={d} data-testid={`attempt-day-${i}`} onChange={(e) => setDay(i, e.target.value)} className="h-8 w-20 px-2 rounded-lg border border-slate-300 text-[13px] focus:outline-none focus:ring-2 focus:ring-indigo-500/30" />
              {f.attemptDays.length > 1 && (
                <button type="button" onClick={() => removeDay(i)} data-testid={`attempt-remove-${i}`} className="p-1 text-red-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
              )}
            </div>
          ))}
          <button type="button" onClick={addDay} data-testid="attempt-add" className="text-[12.5px] text-indigo-600 hover:text-indigo-700 font-medium inline-flex items-center gap-1">
            <Plus className="h-3.5 w-3.5" /> Add attempt
          </button>
        </div>
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Calling hours — start">
          <input type="time" data-testid="window-start" value={f.callWindowStart} onChange={(e) => set("callWindowStart")(e.target.value)} className={inputCls} />
        </Field>
        <Field label="Calling hours — end">
          <input type="time" data-testid="window-end" value={f.callWindowEnd} onChange={(e) => set("callWindowEnd")(e.target.value)} className={inputCls} />
        </Field>
      </div>

      <label className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2.5">
        <span className="text-[13px] text-slate-700">Allow weekend calling</span>
        <Switch checked={f.weekendCallingAllowed} onCheckedChange={(v) => set("weekendCallingAllowed")(v)} data-testid="weekend-toggle" />
      </label>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Voicemail behavior">
          <select data-testid="voicemail-select" value={f.voicemailBehavior} onChange={(e) => set("voicemailBehavior")(e.target.value)} className={inputCls}>
            {(meta.voicemailBehaviors || []).map((v) => <option key={v.key} value={v.key}>{v.label}</option>)}
          </select>
        </Field>
        <Field label="Timezone">
          <input data-testid="timezone-input" value={f.timezone} onChange={(e) => set("timezone")(e.target.value)} className={inputCls} />
        </Field>
      </div>
      <Field label="Transfer number" hint="Office line used to bridge qualified callers to a human.">
        <input data-testid="transfer-input" value={f.transferNumber} onChange={(e) => set("transferNumber")(e.target.value)} placeholder="+1 555 123 4567" className={inputCls} />
      </Field>

      <div className="mt-2 pt-4 border-t border-slate-200">
        <div className="text-[12px] font-bold uppercase tracking-wider text-slate-500 mb-2.5">Sales automations</div>

        <label className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2.5">
          <span className="text-[13px] text-slate-700 pr-3">Auto-send invoice with a pay link when the customer accepts<br /><span className="text-[11.5px] text-slate-400">Converts the accepted estimate to an invoice and texts/emails the pay link.</span></span>
          <Switch checked={f.autoSendOnAccept} onCheckedChange={(v) => set("autoSendOnAccept")(v)} data-testid="auto-send-toggle" />
        </label>

        <label className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2.5 mt-2.5">
          <span className="text-[13px] text-slate-700 pr-3">Create a follow-up task when an AI-sent invoice is paid<br /><span className="text-[11.5px] text-slate-400">Fires on the first payment (online or in-office) and assigns it to the staff member below.</span></span>
          <Switch checked={f.postPaymentTaskEnabled} onCheckedChange={(v) => set("postPaymentTaskEnabled")(v)} data-testid="post-payment-toggle" />
        </label>

        {f.postPaymentTaskEnabled && (
          <div className="grid grid-cols-2 gap-3 mt-2.5">
            <Field label="Assign task to">
              <select data-testid="post-payment-assignee-select" value={f.postPaymentTaskAssigneeId} onChange={(e) => set("postPaymentTaskAssigneeId")(e.target.value)} className={inputCls}>
                <option value="">Select staff member…</option>
                {staff.map((u) => <option key={u.id} value={u.id}>{u.name}{u.role ? ` · ${u.role}` : ""}</option>)}
              </select>
            </Field>
            <Field label="Due in (days)">
              <input type="number" min="0" data-testid="post-payment-due-input" value={f.postPaymentTaskDueDays} onChange={(e) => set("postPaymentTaskDueDays")(Math.max(0, parseInt(e.target.value || "0", 10)))} className={inputCls} />
            </Field>
          </div>
        )}
        {f.postPaymentTaskEnabled && !f.postPaymentTaskAssigneeId && (
          <div className="text-[11.5px] text-amber-600 mt-1.5">Pick a staff member, or the task won't be created.</div>
        )}
      </div>
    </Drawer>
  );
}
